import React, { useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import './TrafficButtons.css';

import { ReactComponent as Radio0Dark } from '../../assets/icons/radio0.svg';
import { ReactComponent as Radio1Dark } from '../../assets/icons/radio1.svg';
import { ReactComponent as Radio2Dark } from '../../assets/icons/radio2.svg';
import { ReactComponent as Radio0Light } from '../../assets/icons/radio0-light.svg';
import { ReactComponent as Radio1Light } from '../../assets/icons/radio1-light.svg';
import { ReactComponent as Radio2Light } from '../../assets/icons/radio2-light.svg';
import { useSites } from '../../contexts/SitesContext';

const TOOLTIP_SIZE = { width: 260, height: 64, margin: 12 };
export const TRAFFIC_STATUS_OPTIONS = [
  { label: 'Disabled', title: 'Verification off', level: 'disabled' },
  { label: 'Monitoring', title: 'Monitoring only, bots not blocked', level: 'detection_only' },
  { label: 'Active', title: 'Monitoring and stopping bots', level: 'true_traffic' },
];

const getSelectedIndexFromLevel = (level) => {
  if (level === 'disabled') return 0;
  if (level === 'detection_only') return 1;
  if (level === 'true_traffic') return 2;
  return 0;
};

const useTrafficRadioGlyphs = () => {
  const { isDarkMode } = useTheme();

  return isDarkMode
    ? [Radio0Dark, Radio1Dark, Radio2Dark]
    : [Radio0Light, Radio1Light, Radio2Light];
};

const TrafficButtonsGlyph = ({ selected }) => {
  const [Radio0, Radio1, Radio2] = useTrafficRadioGlyphs();

  if (selected === 0) return <Radio0 style={{ width: '100%' }} />;
  if (selected === 1) return <Radio1 style={{ width: '100%' }} />;
  if (selected === 2) return <Radio2 style={{ width: '100%' }} />;
  return null;
};

const TrafficButtonsLabel = ({ selected }) => {
  const label = TRAFFIC_STATUS_OPTIONS[selected]?.label ?? '';
  return (
    <small className="traffic-buttons__label margin-none hide-mobile">
      {label}
    </small>
  );
};

const getTooltipPosition = (rect) => {
  if (typeof window === 'undefined') {
    return { x: 0, y: 0 };
  }

  const desiredX = rect.left + (rect.width / 2) - (TOOLTIP_SIZE.width / 2);
  const desiredY = rect.bottom + 10;

  return {
    x: Math.min(
      Math.max(TOOLTIP_SIZE.margin, desiredX),
      window.innerWidth - TOOLTIP_SIZE.width - TOOLTIP_SIZE.margin
    ),
    y: Math.min(
      Math.max(TOOLTIP_SIZE.margin, desiredY),
      window.innerHeight - TOOLTIP_SIZE.height - TOOLTIP_SIZE.margin
    ),
  };
};

const TrafficButtons = ({direction, site, className = '', showLabel = true, onSelectionChange}) => {
  const ref = useRef(null);
  const touchTooltipTimeoutRef = useRef(null);
  let [selected, setSelected] = React.useState(-1);
  const [hoveredIndex, setHoveredIndex] = React.useState(null);
  const [tooltipPosition, setTooltipPosition] = React.useState(null);
  const { setDetectionLevel } = useSites();

  const getIndexFromPosition = (clientX) => {
    if (!ref.current) return null;
    const rect = ref.current.getBoundingClientRect();
    const relativeX = clientX - rect.left;
    if (relativeX < 0 || relativeX > rect.width) return null;
    const segment = rect.width / 3;
    if (relativeX < segment) return 0;
    if (relativeX < segment * 2) return 1;
    return 2;
  };

  const handleClick = (e) => {
    const idx = getIndexFromPosition(e.clientX);
    if (idx === null) return;
    setSelected(idx);
    const level = TRAFFIC_STATUS_OPTIONS[idx].level;
    onSelectionChange?.(level);

    let token = localStorage.getItem('token');

    if (token) {
      fetch(process.env.REACT_APP_SERVER_URL + '/api/set-detection-level', {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({website: site.website, level})
      })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            setDetectionLevel(site.id, level);
          }
        })
    }
  };

  const handleHover = (e) => {
    const idx = getIndexFromPosition(e.clientX);
    if (idx === null) {
      setHoveredIndex(null);
      setTooltipPosition(null);
      return;
    }
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) {
      setHoveredIndex(null);
      setTooltipPosition(null);
      return;
    }
    setHoveredIndex(idx);
    setTooltipPosition(getTooltipPosition(rect));
  };

  const showTouchTooltip = (touch) => {
    const idx = getIndexFromPosition(touch.clientX);
    if (idx === null) {
      setHoveredIndex(null);
      setTooltipPosition(null);
      return;
    }
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) {
      setHoveredIndex(null);
      setTooltipPosition(null);
      return;
    }

    setHoveredIndex(idx);
    setTooltipPosition(getTooltipPosition(rect));
    if (touchTooltipTimeoutRef.current) {
      window.clearTimeout(touchTooltipTimeoutRef.current);
    }
    touchTooltipTimeoutRef.current = window.setTimeout(() => {
      setHoveredIndex(null);
      setTooltipPosition(null);
    }, 1200);
  };

  useEffect(() => {
    setSelected(getSelectedIndexFromLevel(site.detectionLevel));
  }, [site.detectionLevel]);

  useEffect(() => () => {
    if (touchTooltipTimeoutRef.current) {
      window.clearTimeout(touchTooltipTimeoutRef.current);
    }
  }, []);

  return (
    <div className={`traffic-buttons${direction === 'row' ? ' traffic-buttons--row' : ''}${className ? ` ${className}` : ''}`}
         onMouseLeave={() => {
           setHoveredIndex(null);
           setTooltipPosition(null);
         }}>
      <div ref={ref}
           className="radio relative"
           onClick={handleClick}
           onMouseEnter={handleHover}
           onMouseMove={handleHover}
           onTouchStart={(event) => {
             const touch = event.touches[0];
             if (touch) showTouchTooltip(touch);
           }}
           onTouchMove={(event) => {
             const touch = event.touches[0];
             if (touch) showTouchTooltip(touch);
           }}
           onTouchEnd={() => {
             if (touchTooltipTimeoutRef.current) {
               window.clearTimeout(touchTooltipTimeoutRef.current);
             }
             touchTooltipTimeoutRef.current = window.setTimeout(() => {
               setHoveredIndex(null);
               setTooltipPosition(null);
             }, 900);
           }}
           onMouseLeave={() => {
             setHoveredIndex(null);
             setTooltipPosition(null);
           }}>
        <TrafficButtonsGlyph selected={selected} />

        {hoveredIndex !== null && tooltipPosition && (
          <div className={`tooltip tooltip-floating no-triangle status-tooltip ${hoveredIndex === selected ? 'active' : ''}`}
               style={{ left: tooltipPosition.x,
                        top: tooltipPosition.y }}>
            <span className="tooltip-title">
              {TRAFFIC_STATUS_OPTIONS[hoveredIndex].title}
            </span>
          </div>
        )}
      </div>
      {showLabel && <TrafficButtonsLabel selected={selected} />}
    </div>
  );
}

export const TrafficButtonsPreview = ({ level, className = '', showLabel = true }) => {
  const selected = getSelectedIndexFromLevel(level);
  return (
    <div className={`traffic-buttons traffic-buttons--static${className ? ` ${className}` : ''}`}>
      <div className="radio relative" aria-hidden="true">
        <TrafficButtonsGlyph selected={selected} />
      </div>
      {showLabel && <TrafficButtonsLabel selected={selected} />}
    </div>
  );
};

export default TrafficButtons;
